import { Link } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Clock, Star, Users, MapPin, ArrowRight, Sparkles, Heart } from 'lucide-react';
import { useState } from 'react';

interface Tour {
  id: string;
  name: string;
  slug: string;
  short_description: string;
  main_image_url: string;
  adult_price: number;
  child_price?: number;
  base_price?: number;
  duration: string;
  max_capacity: number;
  location?: string;
  is_featured: boolean;
  pricing_type: string;
  features?: string[];
  tour_categories?: {
    name: string;
  };
}

interface TourCardProps {
  tour: Tour;
}

export const TourCard = ({ tour }: TourCardProps) => {
  const [isLiked, setIsLiked] = useState(false);
  const price = tour.pricing_type === 'package' ? tour.base_price || 0 : tour.adult_price;

  return (
    <Link to={`/tour/${tour.slug}`} className="group block h-full">
      <Card className="relative overflow-hidden h-full border-0 shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 bg-card">
        {/* Image Section */}
        <div className="relative aspect-[4/3] overflow-hidden">
          {/* Image with Zoom Effect */}
          <img
            src={tour.main_image_url || '/placeholder.svg'}
            alt={tour.name}
            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
          />
          
          {/* Gradient Overlay */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent opacity-60 group-hover:opacity-80 transition-opacity duration-500" />
          
          {/* Animated Shimmer Effect on Hover */}
          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000 ease-in-out" />

          {/* Price Badge - Animated */}
          <div className="absolute top-4 right-4 z-10">
            <div className="relative">
              {/* Glow Effect */}
              <div className="absolute inset-0 bg-gradient-to-r from-tropical to-palm blur-lg opacity-50 group-hover:opacity-80 transition-opacity rounded-full"></div>
              <div className="relative bg-gradient-to-r from-tropical to-palm text-white px-5 py-3 rounded-2xl shadow-xl group-hover:scale-110 transition-transform duration-300">
                <p className="text-[10px] font-medium uppercase tracking-wider opacity-90">From</p>
                <p className="text-2xl font-bold leading-none">${price}</p>
              </div>
            </div>
          </div>

          {/* Like Button */}
          <button
            onClick={(e) => {
              e.preventDefault();
              setIsLiked(!isLiked);
            }}
            className="absolute top-4 left-4 z-10 w-10 h-10 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center hover:bg-white/40 transition-all duration-300 group/heart"
          >
            <Heart 
              className={`w-5 h-5 transition-all duration-300 ${
                isLiked 
                  ? 'fill-secondary text-secondary scale-110' 
                  : 'text-white group-hover/heart:text-secondary'
              }`} 
            />
          </button>

          {/* Category Badge */}
          {tour.tour_categories && (
            <Badge className="absolute bottom-16 left-4 bg-primary/90 backdrop-blur-sm text-white border-0 shadow-lg">
              {tour.tour_categories.name}
            </Badge>
          )}

          {/* Featured Badge with Animation */}
          {tour.is_featured && (
            <div className="absolute top-16 left-4 z-10">
              <Badge className="bg-gradient-to-r from-secondary to-red-500 text-white border-0 shadow-lg animate-pulse">
                <Sparkles className="w-3 h-3 mr-1" />
                Featured
              </Badge>
            </div>
          )}

          {/* Location Badge */}
          {tour.location && (
            <div className="absolute bottom-4 left-4 right-4 flex items-center gap-2">
              <div className="flex items-center gap-2 text-white bg-black/40 backdrop-blur-md px-3 py-1.5 rounded-full">
                <MapPin className="w-4 h-4 text-tropical" />
                <span className="text-sm font-medium">{tour.location}</span>
              </div>
            </div>
          )}
        </div>

        {/* Content Section */}
        <CardContent className="p-5">
          {/* Title & Description */}
          <div className="mb-4">
            <h3 className="text-lg font-bold mb-2 text-foreground group-hover:text-primary transition-colors duration-300 line-clamp-2 leading-tight">
              {tour.name}
            </h3>
            {tour.short_description && (
              <p className="text-sm text-muted-foreground line-clamp-2 leading-relaxed">
                {tour.short_description}
              </p>
            )}
          </div>

          {/* Tour Details Grid */}
          <div className="grid grid-cols-3 gap-2 mb-4 p-3 bg-muted/50 rounded-xl">
            <div className="flex flex-col items-center text-center group/stat">
              <div className="w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center mb-1 group-hover/stat:bg-primary/20 transition-colors">
                <Clock className="w-4 h-4 text-primary" />
              </div>
              <span className="text-xs text-muted-foreground font-medium">{tour.duration}</span>
            </div>
            <div className="flex flex-col items-center text-center group/stat border-x border-border/50">
              <div className="w-9 h-9 rounded-full bg-secondary/10 flex items-center justify-center mb-1 group-hover/stat:bg-secondary/20 transition-colors">
                <Users className="w-4 h-4 text-secondary" />
              </div>
              <span className="text-xs text-muted-foreground font-medium">Max {tour.max_capacity}</span>
            </div>
            <div className="flex flex-col items-center text-center group/stat">
              <div className="w-9 h-9 rounded-full bg-tropical/10 flex items-center justify-center mb-1 group-hover/stat:bg-tropical/20 transition-colors">
                <Star className="w-4 h-4 fill-tropical text-tropical" />
              </div>
              <span className="text-xs text-muted-foreground font-medium">4.9/5</span>
            </div>
          </div>

          {/* Features Tags */}
          {tour.features && tour.features.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mb-4">
              {tour.features.slice(0, 2).map((feature, index) => (
                <Badge 
                  key={index} 
                  variant="secondary" 
                  className="text-xs bg-palm/10 text-palm border-0 font-medium"
                >
                  {feature}
                </Badge>
              ))}
              {tour.features.length > 2 && (
                <Badge variant="secondary" className="text-xs bg-muted text-muted-foreground">
                  +{tour.features.length - 2} more
                </Badge>
              )}
            </div>
          )}

          {/* CTA Button */}
          <Button 
            className="w-full bg-gradient-to-r from-primary to-secondary hover:from-primary/90 hover:to-secondary/90 text-white shadow-md hover:shadow-lg transition-all duration-300 group/btn"
          >
            <span className="font-semibold">View Details</span>
            <ArrowRight className="w-4 h-4 ml-2 group-hover/btn:translate-x-1 transition-transform" />
          </Button>
        </CardContent>

        {/* Gradient Border on Hover */}
        <div className="absolute inset-0 rounded-lg border-2 border-transparent group-hover:border-primary/30 transition-colors duration-500 pointer-events-none" />
      </Card>
    </Link>
  );
};
